/* eslint-disable class-methods-use-this */
import * as dateFns from "date-fns";
import { ptBR } from "date-fns/locale";
import _ from "lodash";
import { ECommandResultStatus, ETravelStatus, Maybe, TravelType } from "src/store/graphql/helpers/types.graphql";

class Utils {

  /**
   * Get module link according to the module name
   * @param moduleName Module name
   */
  getModuleLink = (moduleName: string): string => {
    switch (moduleName) {
      case "TOPCON_TECH":
        return "/tech";
      default:
        return "/";
    }
  };

  /**
   * Function to await some amount of time (used to simulate slow requests)
   * @param ms Milliseconds
   * @returns Promise that resolve after a specified milliseconds
   */
  sleep = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

  /**
   * For each asynchronous
   * Allows you to make asynchronous function calls within a for
   * @param arr Target array
   * @param callback Callback async function
   */
  asyncForEach = async (
    arr: Array<any>,
    callback: <T>(arr: T, index: number, array: Array<T>) => Promise<void>
  ): Promise<void> => {
    for (let index = 0; index < arr.length; index++) {
      // eslint-disable-next-line no-await-in-loop
      await callback(arr[index], index, arr);
    }
  };

  /**
   * Convert a date to ISO format
   * @param dateToFormat Date to be formatted
   */
  getDateUTC = (dateToFormat: Date) => {
    const date = new Date(dateToFormat.toString()
      .replace("Z", "-00:00"));
    const dateFormat = date.toLocaleDateString("eng-us", { timeZone: "utc" });

    return new Date(dateFormat);
  };

  /**
   * Format date and hour
   * @param date Date or Hour or Interval
   * @param type Type of first parameter
   * @param options Options to format date
   */
  formatDateIfHave = (
    date: Duration | Date | string,
    type:
      | "fullDate"
      | "time"
      | "timeWithoutSeconds"
      | "date"
      | "dateWithoutYear"
      | "dateDescriptive"
      | "durationTime"
      | "durationDescriptiveTime"
      | "durationIntervalTime"
      | "year"
      | "fullDateWithoutYear"
      | "fullDateWithoutSeconds"
      | "fullDateWithoutYearAndSeconds",
    options?: {
      durationDescriptiveTime?: {
        onlyMinutes?: boolean;
      };
    }
  ): string | Date => {
    let intervalDate;

    if (!date) return "";
    if (_.isObject(date)) {
      intervalDate = date as Duration;
    } else {
      intervalDate = date as Date | string;
    }

    switch (type) {
      case "fullDate":
        return dateFns.format(new Date(intervalDate), "dd/MM/yy HH:mm:ss");
      case "fullDateWithoutSeconds":
        return dateFns.format(new Date(intervalDate), "dd/MM/yyyy HH:mm");
      case "fullDateWithoutYear":
        return dateFns.format(new Date(intervalDate), "dd/MM HH:mm:ss");
      case "fullDateWithoutYearAndSeconds":
        return dateFns.format(new Date(intervalDate), "dd/MM HH'h'mm'm'");
      case "time":
        return dateFns.format(new Date(intervalDate), "HH:mm:ss");
      case "timeWithoutSeconds":
        return dateFns.format(new Date(intervalDate), "HH'h'mm'm'");
      case "date":
        return dateFns.format(new Date(intervalDate), "dd/MM/yyyy");
      case "dateWithoutYear":
        return dateFns.format(new Date(intervalDate), "dd/MM");
      case "year":
        return dateFns.format(new Date(intervalDate), "yyyy");
      case "dateDescriptive":
        return dateFns.format(new Date(intervalDate), "d 'de' LLLL", {
          locale: ptBR
        });
      case "durationDescriptiveTime": {
        const duration = date as Duration;
        let formattedTime = "";
        const days = duration.days || 0;
        const hours = duration.hours || 0;
        const minutes = duration.minutes || 0;
        const seconds = duration.seconds || 0;

        if (days > 0) formattedTime += `${days}d `;
        if (hours > 0) formattedTime += `${hours}h `;
        if (minutes > 0) formattedTime += `${minutes}m `;
        if (hours === 0 && minutes === 0) {
          if (options && options[type]?.onlyMinutes) {
            formattedTime = "agora mesmo";
          } else {
            formattedTime += `${seconds}s`;
          }
        }

        return formattedTime;
      }
      case "durationIntervalTime":
        // eslint-disable-next-line no-case-declarations
        const initialDate = {
          hours: "00",
          minutes: "00",
          seconds: "00"
        };

        Object.keys(intervalDate)
          .forEach(
            (key) => (initialDate[key] = intervalDate[key].toString()
              .padStart(2, "0"))
          );

        return `${initialDate.hours}:${initialDate.minutes}:${initialDate.seconds}`;

      default:
        return "";
    }
  };

  /**
   * Group and Sort travels according states
   * @param travels Array of travels
   */
  groupAndSortTravels = (travels: Maybe<TravelType>[]): Array<TravelType> => {
    const travelsWithoutInvoiceIssuedAfterNow = travels.filter((travel) => {
      if (travel?.status !== ETravelStatus.INVOICE_ISSUED) return true;

      const invoiceIssueTimeData = new Date(travel.invoiceIssueTime);

      const isFuture = dateFns.isFuture(invoiceIssueTimeData);

      return !isFuture;
    });

    const groupedTravels = travelsWithoutInvoiceIssuedAfterNow.reduce(
      (acc, travel) => {
        if (!travel) return acc;

        const key = travel.status;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(travel);

        return acc;
      },
      {}
    );

    const propToCompare = {
      [ETravelStatus.INVOICE_ISSUED]: "invoiceIssueTime",
      [ETravelStatus.IN_TRANSIT]: "plantDepartureTime",
      [ETravelStatus.IN_CONSTRUCTION]: "constructionStartTime",
      [ETravelStatus.DISCHARGING]: "dischargeStartTime",
      [ETravelStatus.PROGRAMMED]: "scheduledTime",
      [ETravelStatus.PREPARING]: "scheduledTime",
      [ETravelStatus.DELIVERED]: "finishTime",
      [ETravelStatus.CANCELED]: "scheduledTime"
    };

    Object.keys(groupedTravels)
      .forEach((key) => {
        groupedTravels[key].sort((a, b) => {
          const aProp = a[propToCompare[key]];
          const bProp = b[propToCompare[key]];

          if (key === ETravelStatus.PROGRAMMED) {
            return new Date(aProp) < new Date(bProp) ? -1 : 1;
          }

          return new Date(aProp) < new Date(bProp) ? 1 : -1;
        });
      });
    let allTravels: TravelType[] = [];

    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.DISCHARGING] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.IN_CONSTRUCTION] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.IN_TRANSIT] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.INVOICE_ISSUED] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.DELIVERED] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.PREPARING] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.CANCELED] ?? []
    );
    allTravels = allTravels.concat(
      groupedTravels[ETravelStatus.PROGRAMMED] ?? []
    );

    return allTravels;
  };

  /**
   * Get command result according to the command result code
   * @param status Command result code
   */
  getToastStatusType = (status: Maybe<ECommandResultStatus | `${ECommandResultStatus}`> | number | undefined): "success" | "error" | "info" => {

    switch (status) {
      case 0:
      case ECommandResultStatus.SUCCESS:
        return "success";
      case 1:
      case ECommandResultStatus.ERROR:
        return "error";
      default:
        return "info";
    }
  };

  replaceWithDefaultValues = (obj, defaultValues) => Object.entries(defaultValues)
    .reduce((acc, [key, value]) => {
      acc[key] = obj[key] || value;

      return acc;
    }, {});

  /**
   * Get backend base url
   * @param graphQL If the url is for graphql
   */
  getBackendBaseUrl = (graphQL = true) => {

    const {
      protocol,
      hostname
    } = window.location;
    const subdomain = hostname.split(".")[0];
    const domain = hostname.substring(hostname.indexOf(".") + 1);

    return `${protocol}//${subdomain}-api.${domain}${graphQL ? "/customer/graphql" : "/customer"}`;
  };

  /**
   * Format CPF or CNPJ value
   * @param value The CPF or CNPJ value
   */
  formatCpfCnpj = (value: string) => {
    if (value.length === 11) {
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }

    return value.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  };

  /**
   * Remove all special characters from a string
   * @param value The string value
   */
  removeSpecialCharacters = (value: string) => value.replace(/[^a-zA-Z0-9 ]+/g, "");

  /**
   * Validates a CPF (Brazilian ID number).
   * @param cpf - The CPF to be validated.
   * @returns Returns true if the CPF is valid, false otherwise.
   */
  isValidCpf = (cpf: string) => {
    // Remove non-digit characters from the CPF
    const cleanedCPF = cpf.replace(/\D/g, "");

    // Check if the CPF has the correct length
    if (cleanedCPF.length !== 11) {
      return false;
    }

    // Check for repeated digits
    if (/^(\d)\1+$/.test(cleanedCPF)) {
      return false;
    }

    // Validate the CPF using the first verification digit
    let sum = 0;

    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanedCPF.charAt(i), 10) * (10 - i);
    }
    let verificationDigit1 = (sum * 10) % 11;

    if (verificationDigit1 === 10) verificationDigit1 = 0;

    if (parseInt(cleanedCPF.charAt(9), 10) !== verificationDigit1) {
      return false;
    }

    // Validate the CPF using the second verification digit
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanedCPF.charAt(i), 10) * (11 - i);
    }
    let verificationDigit2 = (sum * 10) % 11;

    if (verificationDigit2 === 10) verificationDigit2 = 0;

    if (parseInt(cleanedCPF.charAt(10), 10) !== verificationDigit2) return false;

    // CPF is valid
    return true;
  };

  /**
   * Return the concatenation of two strings with separator
   * If one of the strings is empty, return the other string
   * @param firstString The first string
   * @param secondString The second string
   * @param separator The separator
   */
  concatWithSeparator = (firstString: string, secondString: string, separator) => {
    if (!firstString || firstString === "") return secondString;

    if (!secondString || secondString === "") return firstString;

    return `${firstString}${separator}${secondString}`;
  };
}

export default new Utils();
